import {ChevronDown, ChevronUp, Minus} from 'lucide-react';
import React, {useMemo, useState} from 'react';
import styles from './RankingTable.module.css';

const RankingTable = ({
  data = [],
  columns = [],
  title = null,
  emptyMessage = "No data to display",
  maxHeight = 500,
  minTableWidth = 800,
  showProgressBars = true,
  progressBarConfig = {},
  rowKeyField = 'id',
  defaultSort = null
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: defaultSort?.key || null,
    direction: defaultSort?.direction || 'asc'
  });

  // Default progress bar configuration
  const defaultProgressConfig = {
    primaryColor: '#005A92',
    secondaryColor: '#9ca3af',
    primaryColumns: [], // columns that should use primary color
    ...progressBarConfig
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Check if column should show progress bar
  const shouldShowProgressBar = (column) => {
    return showProgressBars && column.type === 'progress';
  };

  // Get max value for progress bar calculation
  const getMaxValue = (columnKey) => {
    return Math.max(...data.map(item => item[columnKey] || 0));
  };

  // Render cell value with optional progress bar
  const renderCellValue = (value, column) => {
    if (shouldShowProgressBar(column)) {
      const maxValue = getMaxValue(column.key);
      const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
      const isPrimary = sortConfig.key === column.key;

      return (
        <div className={styles.progressCell}>
          <div className={`${styles.progressBackground} ${isPrimary && styles.progressBackgroundSorted}`}>
            <div
              className={`${styles.progressBar} ${isPrimary ? styles.progressBarPrimary : styles.progressBarSecondary}`}
              style={{
                width: `${percentage}%`,
                backgroundColor: isPrimary ? defaultProgressConfig.primaryColor : defaultProgressConfig.secondaryColor
              }}
            />
          </div>
          <span
              style={value === 0 ? {color: progressBarConfig.primaryColor, textShadow: 'none'} : {}}
              className={styles.progressValue}>{column.formatter ? column.formatter(value) : value}</span>
        </div>
      );
    }

    // Handle different cell types
    switch (column.type) {
      case 'id':
        return <span className={styles.idCell}>{column.formatter ? column.formatter(value) : value}</span>;
      case 'custom':
        return column.render ? column.render(value) : value;
      default:
        return <span className="text-gray-700">{column.formatter ? column.formatter(value) : value}</span>;
    }
  };

  // Render sort icon
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Minus className="w-4 h-4" color={progressBarConfig.secondaryColor} />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-4 h-4" color={progressBarConfig.primaryColor}/> :
      <ChevronDown className="w-4 h-4" color={progressBarConfig.primaryColor} />;
  };

  // Get column width class
  const getColumnWidthClass = (column) => {
    if (column.width) {
      return column.width;
    }
    return 'flex-1'; // Default flexible width
  };

  return (
    <div className={styles.tableContainer}>
      {title && (
        <h2 className={styles.tableTitle}>{title}</h2>
      )}

      <div className={styles.tableWrapper}>
        <div
          className={styles.tableScroll}
          style={{
            maxHeight: `${maxHeight}px`,
          }}
        >
          <table
            className={styles.rankingTable}
            style={{ minWidth: `${minTableWidth}px` }}
          >
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`${styles.tableHeader} ${getColumnWidthClass(column)} ${
                      column.sortable ? styles.sortableHeader : ''
                    }`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className={styles.headerContent}>
                      <span>{column.label}</span>
                      {column.sortable && renderSortIcon(column.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => (
                <tr key={index} className={styles.tableRow}>
                  {columns.map((column) => (
                    <td key={column.key} className={`${styles.tableCell} ${getColumnWidthClass(column)}`}>
                      {renderCellValue(item[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sortedData.length === 0 && (
        <div className={styles.emptyState}>
          {emptyMessage}
        </div>
      )}
    </div>
  );
};

export default RankingTable;